import { NavLink } from 'react-router-dom'
import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { MOBILE_NAV, MOBILE_NAV_BOTTOM, MOBILE_NAV_MIDDLE } from '../../utils/data'

import { NotificationContext } from 'components/Notifications/notifications'
import AccountSwitcherMobile from './AccountSwitcher/AccountSwitcherMobile'
import { LOGOUT } from 'reducers/user'
import { hasPermission } from 'hooks/useHasPermission'

function MobileNavItemContent({ item, t, labelKey, badgeText }) {
  return (
    <>
      <span className="nav-icon white-icon" style={{ ...item.style }}>
        {item.icon}
      </span>
      <span style={{ fontSize: 16, marginTop: 2 }} className="mobile-nav__link__label">
        {t(labelKey ?? `shared.menu.${item.key}`)}
      </span>
      {item.isPreview ? (
        <span
          style={{ fontSize: 10, marginLeft: 8, height: 18, marginTop: 0 }}
          className="preview-badge"
        >
          {t('common.earlyPreview')}
        </span>
      ) : null}
      {badgeText != null && (
        <span className="app-badge__text app-badge__text--inline">{badgeText}</span>
      )}
    </>
  )
}

MobileNavItemContent.propTypes = {
  item: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  labelKey: PropTypes.string,
  badgeText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

/**
 * Wraps a mobile nav row in whichever element its item needs (external link,
 * internal route, or button), mirroring NavLinkItem's three branches on desktop.
 */
function MobileNavListItem({ item, t, children }) {
  if (item.external) {
    return (
      <li key={item.key} id={`mobile-nav-item-${item.key}`}>
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mobile-nav__link"
          aria-label={t(`shared.menu.${item.key}`)}
        >
          {children}
        </a>
      </li>
    )
  }

  if (item.path) {
    return (
      <li key={item.key} id={`mobile-nav-item-${item.key}`}>
        <NavLink
          to={item.path}
          exact={item.path === '/'}
          className="mobile-nav__link"
          activeClassName="mobile-nav__link--active"
        >
          {children}
        </NavLink>
      </li>
    )
  }

  return (
    <li key={item.key} id={`mobile-nav-item-${item.key}`}>
      <button
        onClick={item.onClick}
        title={item.title}
        className="mobile-nav__link w-100"
        aria-label={t(`shared.menu.${item.key}`)}
      >
        {children}
      </button>
    </li>
  )
}

MobileNavListItem.propTypes = {
  item: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

function renderMobileNavItem(item, t, notificationsBadgeText = null) {
  return (
    <MobileNavListItem key={item.key} item={item} t={t}>
      <MobileNavItemContent
        item={item}
        t={t}
        labelKey={item.key === 'logout' ? 'Menu.logout' : undefined}
        badgeText={item.key === 'notifications' ? notificationsBadgeText : null}
      />
    </MobileNavListItem>
  )
}

/**
 * Main navigation for mobile.
 * @Tags( layout, shared, mobile, presentational )
 */
const MobileNav = () => {
  const { t } = useTranslation()
  const [menuActive, setMenuActive] = useState(false)
  const dispatch = useDispatch()
  const notificationState = useContext(NotificationContext)

  const notificationsBadgeText = notificationState.stats?.unread
    ? Math.min(99, notificationState.stats?.unread)
    : null

  const toggleMenu = () => {
    function disableScroll() {
      document.body.style.overflowY = 'hidden'
      document.body.style.top = `-${window.scrollY}px`
    }

    function enableScroll() {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.overflowY = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
    }

    if (menuActive === false) {
      disableScroll()
    } else {
      enableScroll()
    }
    setMenuActive(!menuActive)
  }

  return (
    <>
      <nav id="mobile-navbar" className={`mobile-nav ${menuActive ? 'mobile-nav--active' : ''}`}>
        <div className="mobile-nav__content" onClick={() => toggleMenu()}>
          <ul role="navigation">
            {MOBILE_NAV.map(
              (item) =>
                hasPermission(item.permissionName) &&
                renderMobileNavItem(item, t),
            )}

            <hr className="my-4 mx-2 bg-white" style={{ opacity: 0.3 }}></hr>

            {MOBILE_NAV_MIDDLE.map(
              (item) =>
                hasPermission(item.permissionName) &&
                renderMobileNavItem(item, t),
            )}

            <hr className="my-4 mx-2 bg-white" style={{ opacity: 0.3 }}></hr>

            <AccountSwitcherMobile />

            {MOBILE_NAV_BOTTOM.map((item) =>
              renderMobileNavItem(
                item.key === 'logout'
                  ? { ...item, onClick: () => dispatch({ type: LOGOUT }) }
                  : item,
                t,
                notificationsBadgeText,
              ),
            )}
          </ul>
          <div className="mobile-nav__menuIcon-wrapper">
            <div className="mobile-nav__menuIcon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
        <div className="mobile-nav__bg" onClick={() => toggleMenu()}></div>
      </nav>
    </>
  )
}

MobileNav.propTypes = {}

MobileNav.defaultProps = {}

export default MobileNav
